import { Tuple } from "_/types";
import {
  INVALID_SENSOR_TEXTURE_VALUE,
  SENSOR_VALUE_MAX,
  SENSOR_VALUE_MIN,
} from "_/components/analyze/sensor-constants";

export const SENSOR_HISTOGRAM_BIN_COUNT = 1000;
export const SENSOR_HISTOGRAM_BIN_WIDTH =
  (SENSOR_VALUE_MAX - SENSOR_VALUE_MIN) / SENSOR_HISTOGRAM_BIN_COUNT;

export type VisibleHistogramBin = {
  bin: number;
  x: number;
  width: number;
};

const DEFAULT_COLOR_RANGE_WEIGHT = 0.999;

export class SensorHistogramAccumulator {
  readonly bins = new Uint32Array(SENSOR_HISTOGRAM_BIN_COUNT);
  private count = 0;
  private minimum = Infinity;
  private maximum = -Infinity;

  get size() {
    return this.count;
  }

  add(value: number) {
    if (!isUsableSensorValue(value)) return;

    this.minimum = Math.min(this.minimum, value);
    this.maximum = Math.max(this.maximum, value);

    const normalized =
      (value - SENSOR_VALUE_MIN) / (SENSOR_VALUE_MAX - SENSOR_VALUE_MIN);
    const bin = Math.max(
      0,
      Math.min(
        SENSOR_HISTOGRAM_BIN_COUNT - 1,
        Math.floor(normalized * SENSOR_HISTOGRAM_BIN_COUNT)
      )
    );
    this.bins[bin]++;
    this.count++;
  }

  getFullRange(): Tuple<number> {
    if (this.count === 0) {
      return [SENSOR_VALUE_MIN, SENSOR_VALUE_MAX];
    }

    if (this.minimum === this.maximum) {
      const halfBinWidth = SENSOR_HISTOGRAM_BIN_WIDTH / 2;
      return [
        Math.max(SENSOR_VALUE_MIN, this.minimum - halfBinWidth),
        Math.min(SENSOR_VALUE_MAX, this.maximum + halfBinWidth),
      ];
    }

    return [this.minimum, this.maximum];
  }
}

export function getCentralHistogramRange(
  histogram: Uint32Array,
  fullRange: Tuple<number>
): Tuple<number> {
  const count = histogram.reduce((total, binCount) => total + binCount, 0);
  if (count === 0) return fullRange;

  const tailCount = count * ((1 - DEFAULT_COLOR_RANGE_WEIGHT) / 2);
  let cumulative = 0;
  let lowerBin = 0;
  let upperBin = histogram.length - 1;

  for (let i = 0; i < histogram.length; i++) {
    cumulative += histogram[i];
    if (cumulative > tailCount) {
      lowerBin = i;
      break;
    }
  }

  cumulative = 0;
  for (let i = histogram.length - 1; i >= 0; i--) {
    cumulative += histogram[i];
    if (cumulative > tailCount) {
      upperBin = i;
      break;
    }
  }

  return [
    Math.max(
      fullRange[0],
      SENSOR_VALUE_MIN + lowerBin * SENSOR_HISTOGRAM_BIN_WIDTH
    ),
    Math.min(
      fullRange[1],
      SENSOR_VALUE_MIN + (upperBin + 1) * SENSOR_HISTOGRAM_BIN_WIDTH
    ),
  ];
}

export function isUsableSensorValue(value: number): boolean {
  return (
    Number.isFinite(value) &&
    value > SENSOR_VALUE_MIN &&
    value < SENSOR_VALUE_MAX
  );
}

export function getSensorTextureValue(value: number): number {
  return isUsableSensorValue(value) ? value : INVALID_SENSOR_TEXTURE_VALUE;
}

export function getVisibleHistogramBins(
  range: Tuple<number>
): VisibleHistogramBin[] {
  const rangeWidth = range[1] - range[0];
  if (rangeWidth <= 0) return [];

  const firstBin = Math.max(
    0,
    Math.floor((range[0] - SENSOR_VALUE_MIN) / SENSOR_HISTOGRAM_BIN_WIDTH)
  );
  const lastBin = Math.min(
    SENSOR_HISTOGRAM_BIN_COUNT - 1,
    Math.ceil((range[1] - SENSOR_VALUE_MIN) / SENSOR_HISTOGRAM_BIN_WIDTH) - 1
  );

  return Array.from(
    { length: Math.max(0, lastBin - firstBin + 1) },
    (_, index) => {
      const bin = firstBin + index;
      const binMinimum = SENSOR_VALUE_MIN + bin * SENSOR_HISTOGRAM_BIN_WIDTH;
      const visibleMinimum = Math.max(range[0], binMinimum);
      const visibleMaximum = Math.min(
        range[1],
        binMinimum + SENSOR_HISTOGRAM_BIN_WIDTH
      );
      return {
        bin,
        x: (visibleMinimum - range[0]) / rangeWidth,
        width: (visibleMaximum - visibleMinimum) / rangeWidth,
      };
    }
  );
}

export function getRangeInsets(
  domain: Tuple<number>,
  range: Tuple<number>
): Tuple<number> {
  const domainWidth = Math.max(0.0001, domain[1] - domain[0]);
  return [
    Math.max(0, Math.min(100, ((range[0] - domain[0]) / domainWidth) * 100)),
    Math.max(0, Math.min(100, ((domain[1] - range[1]) / domainWidth) * 100)),
  ];
}

export function zoomRangeAtPosition(
  range: Tuple<number>,
  fullRange: Tuple<number>,
  anchorFraction: number,
  scale: number
): Tuple<number> {
  const fullWidth = fullRange[1] - fullRange[0];
  if (fullWidth <= 0) return fullRange;

  const minimumWidth = Math.min(fullWidth, SENSOR_HISTOGRAM_BIN_WIDTH);
  const fraction = Math.max(0, Math.min(1, anchorFraction));
  const currentWidth = Math.max(minimumWidth, range[1] - range[0]);
  const nextWidth = Math.max(
    minimumWidth,
    Math.min(fullWidth, currentWidth * scale)
  );

  if (nextWidth === fullWidth) return fullRange;

  const anchor = range[0] + fraction * currentWidth;
  let minimum = anchor - fraction * nextWidth;
  minimum = Math.max(fullRange[0], Math.min(fullRange[1] - nextWidth, minimum));

  return [minimum, minimum + nextWidth];
}

export function constrainRange(
  range: Tuple<number>,
  outerRange: Tuple<number>,
  minimumWidth: number
): Tuple<number> {
  const outerWidth = outerRange[1] - outerRange[0];
  if (outerWidth <= 0) return outerRange;

  const width = Math.min(
    outerWidth,
    Math.max(minimumWidth, range[1] - range[0])
  );
  const midpoint = (range[0] + range[1]) / 2;
  const minimum = Math.max(
    outerRange[0],
    Math.min(outerRange[1] - width, midpoint - width / 2)
  );
  return [minimum, minimum + width];
}

export function rescaleNestedRange(
  nestedRange: Tuple<number>,
  previousOuterRange: Tuple<number>,
  nextOuterRange: Tuple<number>
): Tuple<number> {
  const isAtEdge = (value: number, edge: number) =>
    Math.abs(value - edge) <= Number.EPSILON * Math.max(1, Math.abs(edge));
  const lower = isAtEdge(nestedRange[0], previousOuterRange[0])
    ? nextOuterRange[0]
    : nestedRange[0];
  const upper = isAtEdge(nestedRange[1], previousOuterRange[1])
    ? nextOuterRange[1]
    : nestedRange[1];

  return [
    Math.max(nextOuterRange[0], Math.min(nextOuterRange[1], lower)),
    Math.max(nextOuterRange[0], Math.min(nextOuterRange[1], upper)),
  ];
}
